// src/pages/AboutPage.jsx
import { motion } from "framer-motion";
import { Zap, Target, Eye, ChevronRight } from "lucide-react";
import data from "../data/content.json";
import { useCmsDoc } from "../hooks/useCmsDoc";
import { ResolveCmsIcon } from "../components/ResolveCmsIcon";

const DEFAULT_VALORES = data.aboutPage?.Philosophy?.valores || [];

const AboutPage = () => {
  const { data: aboutPage } = useCmsDoc("site", "about", data.aboutPage);
  if (!aboutPage) return null;

  const philosophy = aboutPage.Philosophy || {};
  const valores = Array.isArray(philosophy.valores) && philosophy.valores.length
    ? philosophy.valores
    : DEFAULT_VALORES;

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="bg-[#fafcfb] min-h-screen font-body selection:bg-brand-accent selection:text-brand-dark"
    >
      {/* 1. HERO EDITORIAL (ESTANDARIZADO) */}
      <header className="relative pt-40 pb-20 bg-brand-dark overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" 
             style={{ backgroundImage: 'linear-gradient(#39ff14 1px, transparent 1px), linear-gradient(90deg, #39ff14 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-accent/10 blur-[120px] rounded-full" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-brand-accent" />
              <span className="text-brand-accent font-body font-bold uppercase tracking-[0.4em] text-[11px] md:text-sm">Perfil Institucional</span>
            </motion.div>
            
            <motion.h1 
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
              className="font-display text-[clamp(2.8rem,10vw,3.5rem)] text-white font-black leading-[1.1] md:leading-[1] tracking-tighter flex flex-wrap items-center gap-x-4 md:gap-x-6"
            >
              <span className="block">{aboutPage.title}</span>
              <span className="block font-display text-[clamp(3.25rem,15vw,3rem)] text-brand-accent italic font-light">{aboutPage.subtitle}</span>
            </motion.h1>

            <motion.p 
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
              className="text-white/80 text-lg md:text-2xl font-medium mt-1.5 max-w-5xl border-l-2 border-brand-accent/30 pl-6"
            >
              {aboutPage.description}
            </motion.p>
          </div>
        </div>
      </header>

      {/* 2. EL NÚCLEO ESTRATÉGICO (Misión, Visión y Valores Integrados) */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            
            {/* COLUMNA IZQUIERDA: Misión y Visión con estilo de Propuesta */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {/* MISIÓN */}
              <motion.div whileHover={{ x: 10 }} className="p-10 bg-[#f8faf9] rounded-[40px] border border-brand-soft shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                    <Target className="text-brand-primary" size={20} />
                  </div>
                  <h2 className="font-display text-3xl font-bold text-brand-dark tracking-tight">Misión</h2>
                </div>
                {/* ESTILO PROPUESTA APLICADO */}
                <motion.p 
                  initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
                  className="text-brand-dark/80 text-lg md:text-1xl font-medium mt-1.5 max-w-5xl border-l-2 border-brand-accent/30 pl-6"
                >
                  "{aboutPage.Philosophy.mision}"
                </motion.p>
              </motion.div>

              {/* VISIÓN */}
              <motion.div whileHover={{ x: 10 }} className="p-10 bg-brand-dark rounded-[40px] shadow-2xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/5 blur-2xl" />
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-brand-accent flex items-center justify-center">
                    <Eye className="text-brand-dark" size={20} />
                  </div>
                  <h2 className="font-display text-3xl font-bold tracking-tight">Visión</h2>
                </div>
                {/* ESTILO PROPUESTA APLICADO */}
                <motion.p 
                  initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
                  className="text-white/80 text-lg md:text-1xl font-medium mt-1.5 max-w-5xl border-l-2 border-brand-accent/30 pl-6"
                >
                  "{aboutPage.Philosophy.vision}"
                </motion.p>
              </motion.div>
            </div>

            {/* COLUMNA DERECHA: VALORES (Estilo Propuesta) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="lg:col-span-7 bg-white rounded-[50px] p-8 md:p-14 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] border border-brand-soft flex flex-col justify-center"
            >
              <div className="mb-12">
                <h2 className="font-display text-5xl md:text-6xl font-extrabold text-brand-dark tracking-tighter">
                  {philosophy.valoresTitle || "Valores"}
                </h2>
                <p className="font-body text-brand-primary font-bold uppercase tracking-[0.2em] text-[10px] mt-2">
                  {philosophy.valoresSubtitle || "Cultura de Ingeniería Humana"}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
                {valores.map((val, i) => (
                  <div key={val.label || i} className="flex gap-5 group items-center">
                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#f8faf9] flex items-center justify-center group-hover:bg-brand-accent transition-all duration-500 shadow-sm">
                      <ResolveCmsIcon
                        icon={val.icon || "lucide:Gem"}
                        size={22}
                        className="text-brand-primary group-hover:text-brand-dark transition-colors"
                        alt={val.label}
                      />
                    </div>
                    <div>
                      <h4 className="text-brand-dark font-display font-bold text-xl leading-none mb-1">{val.label}</h4>
                      <p className="text-brand-dark/75 font-body font-italic text-[10px] uppercase font-bold tracking-widest">{val.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. LIDERAZGO (DR. JARQUÍN) */}
      <section className="py-24 bg-[#f8faf9]">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} className="lg:col-span-5">
              {/* Retrato 2:3 (p. ej. 800×1200) — el marco anterior 7:5 recortaba la foto */}
              <div className="relative aspect-[2/3] max-w-[420px] mx-auto lg:mx-0 rounded-[38px] overflow-hidden shadow-2xl border-[4px] border-white bg-brand-soft">
                <img
                  src={aboutPage.leadership.image}
                  className="w-full h-full object-cover object-top"
                  alt="Dr. Jarquín"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-transparent to-transparent" />
                {/* Overlay solo en desktop — en mobile el rostro queda despejado */}
                <div className="absolute bottom-10 left-10 hidden lg:block">
                  <span className="bg-brand-accent text-brand-dark text-[10px] font-bold px-3 py-1 rounded-full uppercase mb-3 inline-block">Investigador Principal</span>
                  <h3 className="text-white font-display text-2xl font-bold">{aboutPage.leadership.name}</h3>
                </div>
              </div>
              {/* Nombre e insignia debajo de la imagen en mobile */}
              <div className="lg:hidden mt-4 px-1 flex items-center gap-3 flex-wrap">
                <span className="bg-brand-accent text-brand-dark text-[10px] font-bold px-3 py-1 rounded-full uppercase shrink-0">Investigador Principal</span>
                <h3 className="text-brand-dark font-display text-xl font-bold">{aboutPage.leadership.name}</h3>
              </div>
            </motion.div>
            
            <div className="lg:col-span-7 space-y-8">
              <h3 className="font-display text-4xl font-bold text-brand-dark leading-tight border-l-4 border-brand-accent pl-8">
                Ciencia que trasciende fronteras</h3>
              <p className="font-body text-xl text-brand-dark/70 text-justify leading-relaxed font-light">
                {aboutPage.accreditation.extra}
              </p>
                <h3 className="font-display text-4xl font-bold text-brand-dark leading-tight border-l-4 border-brand-accent pl-8">{aboutPage.leadership.slogan}</h3>
              <p className="font-body text-xl text-brand-dark/70 text-justify leading-relaxed font-light">
                {aboutPage.leadership.bio}
              </p>
              <div className="bg-brand-dark p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
                <Zap className="absolute -right-8 -top-8 w-32 h-32 text-brand-accent opacity-5 group-hover:opacity-10 transition-opacity" />
                <p className="font-body text-brand-accent font-medium text-xl leading-relaxed italic">
                   {aboutPage.leadership.extra}{" "}
                   {(() => {
                     const label =
                       aboutPage.leadership.linkLabel ||
                       data.aboutPage?.leadership?.linkLabel ||
                       "Nuestras investigaciones";
                     const href =
                       aboutPage.leadership.linkUrl ||
                       data.aboutPage?.leadership?.linkUrl ||
                       "https://ckducureconsortium.org/";
                     if (!label && !href) return null;
                     const external = String(href).startsWith("http");
                     return (
                       <a
                         href={href}
                         className="underline"
                         target={external ? "_blank" : undefined}
                         rel={external ? "noopener noreferrer" : undefined}
                       >
                         {label}
                       </a>
                     );
                   })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* 4. IDENTIDAD VISUAL (SIMETRÍA TOTAL) */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10">
            
            {/* TARJETA AGDYSA */}
            <div className="bg-[#edf2f7] rounded-[50px] p-8 md:p-12 border border-brand-soft hover:shadow-xl transition-all flex flex-col justify-between">
              <div className="flex flex-col md:flex-row gap-8 items-start mb-10">
                {/* Contenedor del Logo - Ancho fijo para alineación perfecta */}
                <div className="w-32 flex-shrink-0">
                  <img 
                    src={aboutPage.company.agdysa_logo} 
                    className="w-full h-auto object-contain" 
                    alt="AGDYSA" 
                  />
                </div>
                {/* Contenedor de Texto */}
                <div className="flex-1">
                  <p className="font-body text-xl text-justify text-dark-brand/80 leading-relaxed italic">
                    {aboutPage.company.agdysa}
                  </p>
                </div>
              </div>
              
              <div className="pt-6 border-t border-brand-soft flex items-center justify-between mt-auto">
                {aboutPage.company.agdysa_cta_url ? (
                  <a
                    href={aboutPage.company.agdysa_cta_url}
                    className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-brand-primary"
                    target={
                      String(aboutPage.company.agdysa_cta_url).startsWith("http")
                        ? "_blank"
                        : undefined
                    }
                    rel={
                      String(aboutPage.company.agdysa_cta_url).startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {aboutPage.company.agdysa_cta_label || "Estrategia Preventiva"}
                  </a>
                ) : (
                  <span className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-brand-primary">
                    {aboutPage.company.agdysa_cta_label || "Estrategia Preventiva"}
                  </span>
                )}
                <ChevronRight className="text-brand-accent" size={16} />
              </div>
            </div>

            {/* TARJETA LABSYSO */}
            <div className="bg-[#edf7f2] rounded-[50px] p-8 md:p-12 border border-brand-soft hover:shadow-xl transition-all flex flex-col justify-between">
              <div className="flex flex-col md:flex-row gap-8 items-start mb-10">
                {/* Contenedor del Logo - Ancho fijo para alineación perfecta */}
                <div className="w-32 flex-shrink-0">
                  <img 
                    src={aboutPage.company.labsyso_logo} 
                    className="w-full h-auto object-contain" 
                    alt="LABSYSO" 
                  />
                </div>
                {/* Contenedor de Texto */}
                <div className="flex-1">
                  <p className="font-body text-lg text-justify text-dark-brand/80 leading-relaxed italic">
                    {aboutPage.company.labsyso}
                  </p>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 flex items-center justify-between mt-auto">
                {aboutPage.company.labsyso_cta_url ? (
                  <a
                    href={aboutPage.company.labsyso_cta_url}
                    className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-dark-brand"
                    target={
                      String(aboutPage.company.labsyso_cta_url).startsWith("http")
                        ? "_blank"
                        : undefined
                    }
                    rel={
                      String(aboutPage.company.labsyso_cta_url).startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {aboutPage.company.labsyso_cta_label || "ADN Científico"}
                  </a>
                ) : (
                  <span className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-dark-brand">
                    {aboutPage.company.labsyso_cta_label || "ADN Científico"}
                  </span>
                )}
                <ChevronRight className="text-dark-brand group-hover:translate-x-2 transition-transform" size={16} />
              </div>
            </div>

          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/